"use client";

import { useEffect, useMemo, useSyncExternalStore } from "react";
import { useDict } from "@/components/i18n/DictProvider";
import { cn } from "@/components/ui/cn";

type Item = { id: string; text: string };

/** Enough subheads to be worth a list; below this it is noise. */
const MIN_ITEMS = 3;

function slug(text: string, i: number): string {
  const s = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `${s || "section"}-${i + 1}`;
}

/**
 * The article's contents, built on the client from the body's `h2`s (the
 * body is HTML or a document from the backend and carries no anchors, so
 * the ids are given here). Renders nothing for fewer than three subheads.
 * `sidebar`: an open list for the sticky column on laptops; `inline`: a
 * collapsed disclosure at the top of the text for phones and tablets.
 */
const subscribeNever = () => () => {};
const NONE = "";

/** The subheads' texts as one string — a stable snapshot for
 *  useSyncExternalStore (the DOM is server-rendered, so it is there at
 *  hydration); the server snapshot is empty, so the list appears on the
 *  client's first render without a state update in an effect. */
function readHeads(): string {
  return Array.from(document.querySelectorAll<HTMLElement>("[data-reader-text] .prose-rich h2"))
    .map((h) => h.textContent?.trim() ?? "")
    .filter(Boolean)
    .join("\n");
}

export function ArticleContents({ variant }: { variant: "sidebar" | "inline" }) {
  const t = useDict().news;
  const heads = useSyncExternalStore(subscribeNever, readHeads, () => NONE);
  const items: Item[] = useMemo(
    () => (heads ? heads.split("\n").map((text, i) => ({ id: slug(text, i), text })) : []),
    [heads],
  );
  // The body carries no anchors (it is HTML or a document from the backend),
  // so the ids are given here — a DOM write, no React state.
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>("[data-reader-text] .prose-rich h2");
    els.forEach((h, i) => {
      if (!h.id && items[i]) h.id = items[i].id;
    });
  }, [items]);
  if (items.length < MIN_ITEMS) return null;

  const list = (
    <ol className="space-y-1.5">
      {items.map((it) => (
        <li key={it.id}>
          <a
            href={`#${it.id}`}
            className="text-muted-foreground hover:text-foreground block text-sm leading-snug no-underline transition-colors"
          >
            {it.text}
          </a>
        </li>
      ))}
    </ol>
  );

  if (variant === "sidebar")
    return (
      // The rule separates the list from the right unit above it; alone in the
      // column (the library twin passes no unit) there is nothing to separate.
      <nav
        aria-label={t.contents}
        className="border-border/60 border-t pt-4 first:border-t-0 first:pt-0"
      >
        <p className="text-muted-foreground mb-3 text-[11px] font-bold uppercase tracking-[0.14em]">
          {t.contents}
        </p>
        {list}
      </nav>
    );
  return (
    <details
      data-reader-skip=""
      className={cn(
        "border-border/60 bg-muted/40 group/toc mb-6 rounded-lg border px-4 py-3 text-base lg:hidden",
      )}
    >
      <summary className="cursor-pointer list-none text-sm font-semibold [&::-webkit-details-marker]:hidden">
        {t.contents}
        <span className="text-muted-foreground ml-2 font-normal group-open/toc:hidden">
          ({items.length})
        </span>
      </summary>
      <div className="mt-3">{list}</div>
    </details>
  );
}
